// Hook personalizado useFirestore
// Proporciona funciones para interactuar con Firestore de forma reactiva

import { useState, useEffect, useCallback, useRef } from 'react'
import { productsService } from '../services/firestore.service'
import { useBusinessSettingsContext } from './useBusinessSettingsContext'

/**
 * Hook para obtener productos con opciones de filtrado
 * @param {Object} options - Opciones de consulta (category, minPrice, maxPrice, etc.)
 * @param {Array} dependencies - Dependencias para re-ejecutar la consulta
 * @returns {Object} { products, loading, error, refetch }
 * 
 * @example
 * const { products, loading, error } = useProducts({ category: 'sofas' })
 */
export const useProducts = (options = {}, dependencies = []) => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { activeProfile } = useBusinessSettingsContext()
  const optionsKey = JSON.stringify(options || {})
  const optionsRef = useRef({ key: optionsKey, value: options || {} })
  if (optionsRef.current.key !== optionsKey) {
    optionsRef.current = { key: optionsKey, value: options || {} }
  }
  const normalizedOptions = optionsRef.current.value
  const dependenciesKey = JSON.stringify(dependencies || [])

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const commercialProfile = normalizedOptions.commercialProfile === undefined ? activeProfile : normalizedOptions.commercialProfile
      const data = await productsService.getAll({
        ...normalizedOptions,
        commercialProfile
      })

      if (commercialProfile?.rubro === 'muebles') {
        const filtered = (data || []).filter(p => {
          const rubro = p?.commercialProfile?.rubro
          if (!rubro) return true
          return rubro === 'muebles'
        })
        setProducts(filtered)
      } else {
        setProducts(data || [])
      }
    } catch (err) {
      console.error('Error al obtener productos:', err)
      setError(err.message)
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [normalizedOptions, activeProfile])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts, dependenciesKey])

  return {
    products,
    loading,
    error,
    refetch: fetchProducts
  }
}

/**
 * Hook para obtener un producto por ID
 * @param {string} productId - ID del producto
 * @returns {Object} { product, loading, error, refetch }
 */
export const useProduct = (productId) => {
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchProduct = useCallback(async () => {
    if (!productId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await productsService.getById(productId)
      setProduct(data)
    } catch (err) {
      console.error('Error al obtener producto:', err)
      setError(err.message)
      setProduct(null)
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    fetchProduct()
  }, [fetchProduct])

  return {
    product,
    loading,
    error,
    refetch: fetchProduct
  }
}

export default { useProducts, useProduct }

